import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useConfig } from '../context/ConfigContext';
import { publicApi, clientApi } from '../lib/api';
import PageSeo from '../components/PageSeo';
import { Star, ArrowLeft, Loader2, MessageSquare, PenLine } from 'lucide-react';
import { GOOGLE_REVIEWS_URL, GOOGLE_WRITE_REVIEW_URL } from '../lib/contact';
import { toast } from 'sonner';

const StarRating = ({ value, onChange, size = 20, readOnly = false }) => {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex items-center gap-1" data-testid="star-rating">
      {[1, 2, 3, 4, 5].map((n) => {
        const active = (hover || value) >= n;
        return (
          <button
            key={n}
            type="button"
            disabled={readOnly}
            onClick={() => !readOnly && onChange && onChange(n)}
            onMouseEnter={() => !readOnly && setHover(n)}
            onMouseLeave={() => !readOnly && setHover(0)}
            className={readOnly ? 'cursor-default' : 'cursor-pointer'}
            aria-label={`${n} étoile${n > 1 ? 's' : ''}`}
            data-testid={readOnly ? undefined : `star-${n}`}
          >
            <Star
              style={{ width: size, height: size }}
              className={active ? 'text-[#E0A458] fill-[#E0A458]' : 'text-[#D8D2C7]'}
            />
          </button>
        );
      })}
    </div>
  );
};

const ReviewCard = ({ review }) => (
  <div className="bg-white rounded-2xl border border-[#E3DED5] p-6" data-testid="review-card">
    <StarRating value={review.rating} readOnly size={18} />
    <p className="text-[#2A312C] mt-3 leading-relaxed whitespace-pre-line">{review.comment}</p>
    <p className="text-sm text-[#5C6A61] mt-4 font-medium">— {review.display_name}</p>
  </div>
);

export default function ReviewsPage() {
  const { user, login } = useAuth();
  const { config } = useConfig();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [me, setMe] = useState(null); // { eligible, completed_count, review, default_display_name }
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const logo = config?.logo_url;
  const companyName = config?.company_name || 'Mitonner';

  const loadReviews = async () => {
    try {
      const res = await publicApi.getPublishedReviews();
      setReviews(res.data || []);
    } catch (e) {
      // silencieux : page publique
    } finally {
      setLoading(false);
    }
  };

  const loadMine = async () => {
    if (!user) { setMe(null); return; }
    try {
      const res = await clientApi.getMyReview();
      setMe(res.data);
      if (res.data?.review) {
        setRating(res.data.review.rating || 0);
        setComment(res.data.review.comment || '');
        setDisplayName(res.data.review.display_name || res.data.default_display_name || '');
      } else {
        setDisplayName(res.data?.default_display_name || '');
      }
    } catch (e) {
      setMe(null);
    }
  };

  useEffect(() => { loadReviews(); }, []);
  useEffect(() => { loadMine(); /* eslint-disable-next-line */ }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (rating < 1 || rating > 5) { toast.error('Merci de choisir une note entre 1 et 5.'); return; }
    if (!comment.trim()) { toast.error('Merci de saisir un commentaire.'); return; }
    setSubmitting(true);
    try {
      const res = await clientApi.submitReview({ rating, comment: comment.trim(), display_name: displayName.trim() });
      toast.success(res.data?.message || 'Merci pour votre avis !');
      await loadMine();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Impossible d'enregistrer votre avis.");
    } finally {
      setSubmitting(false);
    }
  };

  const statusLabel = (s) => (s === 'published' ? 'Publié' : s === 'hidden' ? 'Masqué par l\'équipe' : 'En attente de validation');
  const statusClass = (s) => (s === 'published' ? 'bg-[#A5BBA8]/20 text-[#4B6B54]' : s === 'hidden' ? 'bg-[#C86B51]/15 text-[#C86B51]' : 'bg-[#E0A458]/15 text-[#9A6B1F]');

  return (
    <div className="min-h-screen bg-[#F9F6F0]">
      <PageSeo title="Avis clients Mitonner | Préparation de repas à domicile" description="Découvrez les avis clients sur Mitonner, service de préparation de repas à domicile." />
      {/* Header */}
      <header className="bg-white border-b border-[#E3DED5] sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="reviews-home-link">
            {logo ? <img src={logo} alt={companyName} className="h-9 w-auto" /> : <span className="font-semibold text-[#2A312C]">{companyName}</span>}
          </Link>
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#5C6A61] hover:text-[#2A312C]" data-testid="reviews-back-btn">
            <ArrowLeft className="w-4 h-4" /> Retour à l'accueil
          </Link>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-12">
        <div className="text-center mb-10">
          <h1 className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl font-medium text-[#2A312C]">Avis clients</h1>
          <p className="text-[#5C6A61] mt-3 max-w-2xl mx-auto">
            Découvrez ce que nos clients pensent de {companyName}.
          </p>
          {(GOOGLE_REVIEWS_URL || GOOGLE_WRITE_REVIEW_URL) && (
            <div className="flex flex-col sm:flex-row gap-3 justify-center mt-6" data-testid="google-reviews-links">
              {GOOGLE_REVIEWS_URL && (
                <a href={GOOGLE_REVIEWS_URL} target="_blank" rel="noopener noreferrer" className="btn-secondary inline-flex items-center justify-center gap-2" data-testid="google-reviews-btn">
                  <Star className="w-4 h-4" /> Consulter nos avis Google
                </a>
              )}
              {GOOGLE_WRITE_REVIEW_URL && (
                <a href={GOOGLE_WRITE_REVIEW_URL} target="_blank" rel="noopener noreferrer" className="btn-outline inline-flex items-center justify-center gap-2" data-testid="google-write-review-btn">
                  <PenLine className="w-4 h-4" /> Déposer un avis Google
                </a>
              )}
            </div>
          )}
        </div>

        {/* Dépôt d'avis */}
        <section className="mb-12" data-testid="review-form-section">
          {!user ? (
            <div className="bg-white rounded-2xl border border-[#E3DED5] p-6 text-center">
              <MessageSquare className="w-8 h-8 text-[#A5BBA8] mx-auto mb-3" />
              <p className="text-[#2A312C] font-medium">Vous êtes client ?</p>
              <p className="text-[#5C6A61] text-sm mt-1 mb-4">Connectez-vous pour partager votre expérience.</p>
              <button onClick={login} className="btn-primary" data-testid="reviews-login-btn">Se connecter</button>
            </div>
          ) : me && !me.eligible ? (
            <div className="bg-white rounded-2xl border border-[#E3DED5] p-6 text-center" data-testid="review-not-eligible">
              <p className="text-[#2A312C] font-medium">Laisser un avis</p>
              <p className="text-[#5C6A61] text-sm mt-1">
                Vous pourrez déposer un avis dès qu'une première prestation aura été réalisée. Merci de votre confiance !
              </p>
            </div>
          ) : me ? (
            <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-[#E3DED5] p-6 space-y-4" data-testid="review-form">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <h2 className="text-lg font-semibold text-[#2A312C]">{me.review ? 'Modifier mon avis' : 'Laisser un avis'}</h2>
                {me.review && (
                  <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${statusClass(me.review.status)}`} data-testid="my-review-status">
                    {statusLabel(me.review.status)}
                  </span>
                )}
              </div>
              {me.review && (
                <p className="text-xs text-[#5C6A61]">
                  Toute modification est soumise à une nouvelle validation avant publication.
                </p>
              )}
              <div>
                <label className="block text-sm font-medium text-[#2A312C] mb-1">Votre note</label>
                <StarRating value={rating} onChange={setRating} size={28} />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#2A312C] mb-1">Votre commentaire</label>
                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  rows={4}
                  maxLength={1000}
                  className="w-full rounded-xl border border-[#E3DED5] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#A5BBA8]"
                  placeholder="Partagez votre expérience…"
                  data-testid="review-comment-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#2A312C] mb-1">Nom d'affichage</label>
                <input
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  maxLength={80}
                  className="w-full rounded-xl border border-[#E3DED5] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#A5BBA8]"
                  placeholder="Ex : Marie D."
                  data-testid="review-name-input"
                />
              </div>
              <button type="submit" disabled={submitting} className="btn-primary inline-flex items-center gap-2" data-testid="review-submit-btn">
                {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                {me.review ? 'Mettre à jour mon avis' : 'Publier mon avis'}
              </button>
            </form>
          ) : (
            <div className="flex justify-center py-6"><Loader2 className="w-6 h-6 animate-spin text-[#A5BBA8]" /></div>
          )}
        </section>

        {/* Liste des avis publiés */}
        <section data-testid="reviews-list">
          {loading ? (
            <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-[#A5BBA8]" /></div>
          ) : reviews.length === 0 ? (
            <div className="text-center text-[#5C6A61] py-10" data-testid="reviews-empty">
              Aucun avis publié pour le moment.
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-5">
              {reviews.map((r) => <ReviewCard key={r.review_id} review={r} />)}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
